import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { defaultPageInfo } from '../../../interface/CommonInterface';
import { IBloodSugarReading } from '../../../interface/BloodSugarInterface';
import { formatDateTime } from '../../../utils/dateTimeUtils';
import { addToast } from '../../../utils/toastNotifications';
import { endpoints } from '../../../utils/URL';
import Pagination from '../../common/Pagination';
import BloodSugarModal from './BloodSugarModal';
import { IPatientTabProps } from '../../../interface/PatientInterface';
import { PatientTabs } from '../../../utils/mappings';
import Loader from '../../common/Loader';
import NoDataView from '../../common/NoData';

const BloodSugarScreen = ({ patientId, tabKey }: IPatientTabProps) => {
  const [pageInfo, setPageInfo] = useState(defaultPageInfo);
  const [bloodSugars, setBloodSugars] = useState<IBloodSugarReading[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getBloodSugarReadings = useCallback(
    async (pageIndex: number) => {
      if (patientId === 0) {
        return;
      }
      setIsLoading(true);
      const url = `${endpoints.bloodSugar.getByPatient}/${patientId}?pageIndex=${pageIndex}&pageSize=10`;
      try {
        const response = await axios.get(url);
        setBloodSugars(response.data.data);

        const responsePageInfo = {
          pageIndex: response.data.pageIndex,
          pageSize: response.data.pageSize,
          pageCount: response.data.pageCount,
        };
        //set page info
        setPageInfo(responsePageInfo);
      } catch (error) {
        addToast('An error occured while getting data', 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [patientId]
  );

  const deleteReading = (readingId: number) => {
    const response = window.confirm('Are you sure you want to delete this reading?');
    if (!response) {
      return;
    }
    addToast('Reading successfully deleted', 'success');
  };

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  useEffect(() => {
    if (tabKey !== PatientTabs.bloodSugar) {
      return;
    }
    getBloodSugarReadings(1);
  }, [patientId, tabKey, getBloodSugarReadings]);

  let bloodSugarModal = (
    <BloodSugarModal
      show={showModal}
      close={toggleModal}
      patientId={patientId}
      callback={() => getBloodSugarReadings(1)}
    />
  );

  const dataView = (
    <div>
      <div className='table-responsive'>
        <table className='table table-hover table-bordered'>
          <thead>
            <tr>
              <th>Glucose</th>
              <th>Reading date</th>
              <th>Actions </th>
            </tr>
          </thead>
          <tbody>
            {bloodSugars.map((bloodSugar) => (
              <tr key={bloodSugar.readingId}>
                <td>{bloodSugar.glucose}</td>
                <td>{formatDateTime(bloodSugar.readingTime)}</td>
                <td>
                  <span
                    className='bi bi-trash custom-group-icon text-danger'
                    title='Delete reading'
                    onClick={() => deleteReading(bloodSugar.readingId)}
                  ></span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPageNumber={pageInfo.pageIndex}
        numberOfPages={pageInfo.pageCount}
        callback={getBloodSugarReadings}
      />
    </div>
  );

  return (
    <section className='mt-5'>
      {bloodSugarModal}
      <h5 className='mb-4'>Blood Sugar Readings</h5>
      <br />
      <button className='btn btn-primary' onClick={toggleModal}>
        Add Reading
      </button>
      <div className='mt-3'></div>
      {isLoading && <Loader />}
      {!isLoading && bloodSugars.length > 0 && dataView}
      {!isLoading && bloodSugars.length === 0 && <NoDataView />}
    </section>
  );
};

export default BloodSugarScreen;
